import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import remove from 'lodash/remove';
import Request from '../../common/api/request';
import ServiceResultFactory from '../../common/api/ServiceResultFactory';
import DocumentValidator from '../exhibitingOrganisation/DocumentValidator';
import { DocumentTypes } from '../../common/Constants';

const documentSortOrders = [
  { docType: 1, sortOrder: 5 },
  { docType: 2, sortOrder: 4 },
  { docType: 3, sortOrder: 3 },
  { docType: 4, sortOrder: 2 },
  { docType: 5, sortOrder: 1 }
];

const customSort = documents => sortBy(
  documents,
  doc => find(
    documentSortOrders,
    order => order.docType === doc.type
  ).sortOrder + doc.displayName
);

const documentUrlWithoutPublishDate = (
  baseUrl, exhibitingOrganisationId,
  documentTypeId
) => (
  `${baseUrl}v1/documents?exhibitingOrganisationId=${exhibitingOrganisationId}&` +
    `documentTypeId=${documentTypeId}`
);

const dropBoxUploadDocUrl = (
  baseUrl, exhibitingOrganisationId,
  locale
) => (
  `${baseUrl}v1/file?exhibitingOrganisationId=${exhibitingOrganisationId}&Type=ExhibitorDocument&Locale=${locale}`
);

const documentUrlWithPublishDate =
  (baseApiUrl, exhibitingOrganisationId, documentTypeId, publishDate) => (
    `${documentUrlWithoutPublishDate(baseApiUrl, exhibitingOrganisationId, documentTypeId)}`
    + `&publishDate=${publishDate}`
  );

const documentUrl = (baseUrl, documentId) => (
  `${baseUrl}v1/documents/${documentId}`
);

const mapDocumentKeyToId = documentTypeKey => findKey(
  DocumentTypes,
  documentTypeValue => documentTypeValue === documentTypeKey
);
const uploadDocument = (baseApiUrl, exhibitingOrgId, documentToUpload, documentType, locale) => {
  const validationResult = DocumentValidator.validate(documentToUpload);
  if (!validationResult.isValid) {
    return Promise.resolve(ServiceResultFactory.fromValidator(locale, validationResult.errors));
  }
  const documentTypeId = mapDocumentKeyToId(documentType);
  const url = `${baseApiUrl}v1/file?Type=Document&ExhibitingOrganisationId=${exhibitingOrgId}&DocumentTypeId=${documentTypeId}`;

  return Request.putFile(url, documentToUpload)
    .then(ServiceResultFactory.fromSuccess)
    .catch(error => ServiceResultFactory.fromError(locale, error, false));
};
const uploadProductDocument = (
  baseApiUrl, exhibitingOrgId, productId, documentToUpload,
  documentType, locale
) => {
  const validationResult = DocumentValidator.validate(documentToUpload);
  if (!validationResult.isValid) {
    return Promise.resolve(ServiceResultFactory.fromValidator(locale, validationResult.errors));
  }
  const documentTypeId = mapDocumentKeyToId(documentType);
  const url = `${baseApiUrl}v1/file?Type=ProductDocument&ExhibitingOrganisationId=${exhibitingOrgId}&ProductId=${productId}&DocumentTypeId=${documentTypeId}`;

  return Request.putFile(url, documentToUpload)
    .then(ServiceResultFactory.fromSuccess)
    .catch(error => ServiceResultFactory.fromError(locale, error, false));
};

const addToList = (
  baseApiUrl, exhibitingOrgId, documentToUpload,
  documentType, _existingDocuments, locale, publishDate
) => {
  const validationResult = DocumentValidator.validate(documentToUpload);
  if (!validationResult.isValid) {
    return Promise.resolve(ServiceResultFactory.fromValidator(locale, validationResult.errors));
  }
  const documentTypeId = mapDocumentKeyToId(documentType);
  const url = documentType === 'press_release' ?
    documentUrlWithPublishDate(baseApiUrl, exhibitingOrgId, documentTypeId, publishDate) :
    documentUrlWithoutPublishDate(baseApiUrl, exhibitingOrgId, documentTypeId);

  return Request.putFile(dropBoxUploadDocUrl(baseApiUrl, exhibitingOrgId, locale), documentToUpload)
    .then(res => Request.postWithFormData(url, { url: res.location }))
    .then(ServiceResultFactory.fromSuccess)
    .catch(error => ServiceResultFactory.fromError(locale, error, true));
};

const removeFromList = (baseServiceUrl, _exhibitingOrgId, documentId, existingDocuments, locale) => {
  const removeUrl = documentUrl(baseServiceUrl, documentId);

  return Request
    .delete(removeUrl)
    .then(ServiceResultFactory.fromSuccess)
    .then(() => {
      const existingDocumentsCopy = [...existingDocuments];
      remove(existingDocumentsCopy, { id: documentId });
      return existingDocumentsCopy;
    })
    .then(updatedDocuments => ServiceResultFactory.fromSuccess({ documents: updatedDocuments }))
    .catch(error => ServiceResultFactory.fromError(locale, error, true));
};

const deleteDocument = (baseUrl, documentId, docUrl, exhibitorId, interfaceLocale) => {
  let storageKey = '';
  let fileType='';

  if(docUrl.includes('/temp-documents/')){
    const index = docUrl.indexOf('exhibitor/document/temp-documents/');
    storageKey = docUrl.substring(docUrl.length, index);
    fileType = 'Document';
    documentId = null;
  }
  else{
    const index = docUrl.indexOf('exhibitor/products/');
    storageKey = docUrl.substring(docUrl.length, index);
    fileType = 'ProductDocument';
  }
  const mutation = `mutation {
    deleteProductDocument(documentId: ${documentId}, storageKey:"${storageKey}", type: ${fileType}, exhibitingOrganisationId: "${exhibitorId}")
  }`;
  return Request.graphqlRequest(`${baseUrl}graphql`, mutation)
  .then(response => ServiceResultFactory.fromGraphql(response, interfaceLocale,'deleteProductDocument'))
  .catch(result => ServiceResultFactory.fromError(interfaceLocale, result, false))
};

const getDocuments = (baseUrl, exhibitingOrganisationId, eventEditionId) => {
  const queryParams = (exhibitingOrganisationId.startsWith('exh-'))
    ? { exhibitingOrganisationId }
    : { organisationGuid: exhibitingOrganisationId, eventEditionId };

  return Request.get(`${baseUrl}v1/documents`, queryParams).then(res => customSort(res.body));
};

const getPublicDocuments = (baseUrl, exhibitingOrganisationId, eventEditionId) => {
  const queryParams = (exhibitingOrganisationId.startsWith('exh-'))
    ? { exhibitingOrganisationId }
    : { organisationGuid: exhibitingOrganisationId, eventEditionId };

  return Request.get(`${baseUrl}v1/documents/public`, queryParams)
    .then(res => customSort(res.body));
};

const getExhibitorDocuments = (baseApiUrl, exhibitingOrganisationId, interfaceLocale) => {
  const url = `${baseApiUrl}graphql`;
  const query = `query {
    exhibitorDocuments(id:"${exhibitingOrganisationId}"){
       id,
       exhibitingOrganisationId,
       url,
       displayName,
       size,
       type,
       publishDate
    }
  }`;
  return Request.graphqlRequest(url, query.replace(/(\r\n| |\n|\r)/gm, ''))
    .then(response => {
      return ServiceResultFactory.fromGraphql(response, interfaceLocale)})
    .catch(result => ServiceResultFactory.fromError(interfaceLocale, result, false));
};

export default {
  addToList, removeFromList, getDocuments, getPublicDocuments, uploadProductDocument, uploadDocument, deleteDocument,
  getExhibitorDocuments
};
